import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import GuestLanding, { buildHomeHrefLangEntries } from './GuestLanding';
import PublicMarketingFooter from './homepage/PublicMarketingFooter';
import PublicMarketingHeader from './homepage/PublicMarketingHeader';
import HOME_BLOG_PREVIEW_POSTS from '../data/homeBlogPreviewPosts';
import {
  buildDynamicBlogTranslationMap,
  mergeBlogPostContent,
  mergeBlogTranslationMaps
} from '../utils/blogTranslations';
import { useAuth } from '../contexts/AuthSharedContext';
import { usePublicAppContext } from '../contexts/PublicAppContext';
import { loadBlogTranslations } from '../utils/loadBlogTranslations';
import { writePublicHomeHandoff } from '../utils/publicHomeHandoff';
import { getPlanCategory, PLAN_CATEGORIES } from '../services/planHelpers';
import { localizeInternalHref, localizeSeoHref } from '../utils/localeRouting';
import { getNextTheme } from '../utils/theme';
import {
  COMPACT_VIEWPORT_ZOOM_QUERY,
  DEFAULT_ZOOM_LEVEL,
  MAX_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  normalizeZoomLevel,
  readStoredZoomLevel,
  resolveAppliedZoomLevel
} from '../utils/zoomLevel';
import {
  HOME_MAIN_CONTENT_LABEL_BN,
  HOME_SKIP_TO_MAIN_LABEL_BN
} from '../data/i18n/publicChrome.bn.js';
import {
  HOME_MAIN_CONTENT_LABEL_HI,
  HOME_SKIP_TO_MAIN_LABEL_HI
} from '../data/i18n/publicChrome.hi.js';
import {
  HOME_MAIN_CONTENT_LABEL_PT,
  HOME_SKIP_TO_MAIN_LABEL_PT
} from '../data/i18n/publicChrome.pt.js';
import '../styles/App.css';
const GUEST_TRIAL_IMAGE = '/DESIGN.jpg';
const GUEST_TRIAL_FILENAME = 'guest-trial.jpg';
const GUEST_TRIAL_MIME = 'image/jpeg';
const GUEST_TRIAL_PLATFORMS = [
  { id: 'shutterstock', label: 'Shutterstock' },
  { id: 'adobeStock', label: 'Adobe Stock' },
  { id: 'istock', label: 'iStock' },
  { id: 'gettyImages', label: 'Getty Images' },
  { id: 'freepik', label: 'Freepik' },
  { id: 'vecteezy', label: 'Vecteezy' },
  { id: 'dreamstime', label: 'Dreamstime' },
  { id: 'depositphotos', label: 'Depositphotos' },
  { id: 'pond5', label: 'Pond5' },
  { id: 'oneTwoThreeRF', label: '123RF' }
];
const HOME_SKIP_TO_MAIN_LABELS = {
  en: 'Skip to main content',
  tr: 'Ana içeriğe geç',
  es: 'Saltar al contenido principal',
  fr: 'Aller au contenu principal',
  de: 'Zum Hauptinhalt springen',
  it: 'Vai al contenuto principale',
  id: 'Lewati ke konten utama',
  th: 'ข้ามไปยังเนื้อหาหลัก',
  bn: HOME_SKIP_TO_MAIN_LABEL_BN,
  hi: HOME_SKIP_TO_MAIN_LABEL_HI,
  pt: HOME_SKIP_TO_MAIN_LABEL_PT
};
const HOME_MAIN_CONTENT_LABELS = {
  en: 'Home content',
  tr: 'Ana sayfa içeriği',
  es: 'Contenido principal',
  fr: 'Contenu principal',
  de: 'Hauptinhalt',
  it: 'Contenuto della home',
  id: 'Konten beranda',
  th: 'เนื้อหาหน้าแรก',
  bn: HOME_MAIN_CONTENT_LABEL_BN,
  hi: HOME_MAIN_CONTENT_LABEL_HI,
  pt: HOME_MAIN_CONTENT_LABEL_PT
};

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const resolveLocalizedUiLabel = (copyMap, language) => (
  copyMap[String(language || '').trim().toLowerCase()] || copyMap.en
);

const normalizePreviewPosts = (posts = []) => (
  [...posts]
    .sort((a, b) => {
      const priorityA = Number.isFinite(Number(a?.featuredPriority)) ? Number(a.featuredPriority) : 999;
      const priorityB = Number.isFinite(Number(b?.featuredPriority)) ? Number(b.featuredPriority) : 999;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(b.date) - new Date(a.date);
    })
    .slice(0, 5)
    .map((post) => {
      let shortExcerpt = post.excerpt || '';
      if (shortExcerpt.includes('.')) {
        shortExcerpt = `${shortExcerpt.split('.')[0]}.`;
      } else if (shortExcerpt.length > 100) {
        shortExcerpt = `${shortExcerpt.substring(0, 97)}...`;
      }

      return {
        ...post,
        image: post.featuredImage || post.image,
        imageAlt: post.featuredImageAlt || post.imageAlt,
        excerpt: shortExcerpt
      };
    })
);

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

const scheduleIdleTask = (callback) => {
  if (typeof window === 'undefined') {
    callback();
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(() => callback(), { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }

  const timeoutId = window.setTimeout(() => callback(), 350);
  return () => window.clearTimeout(timeoutId);
};

let authenticatedHomeWarmupPromise = null;
const warmAuthenticatedHomeRuntime = () => {
  if (!authenticatedHomeWarmupPromise) {
    authenticatedHomeWarmupPromise = Promise.allSettled([
      import('../AppContent'),
      import('./UserProfile'),
      import('./MetadataPanel'),
      import('./HistoryPanel'),
      import('./AppsPanel'),
      import('../services/historyService')
    ]);
  }
  return authenticatedHomeWarmupPromise;
};

const HOME_HREFLANG_ENTRIES = buildHomeHrefLangEntries();

const HomePageInner = () => {
  const {
    theme,
    preferencesHydrated,
    setTheme,
    language,
    setLanguage,
    texts
  } = usePublicAppContext();
  const { user, loading } = useAuth();
  const authWarmupStartedRef = useRef(false);
  const blogPreviewRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
  const [zoomPreferenceHydrated, setZoomPreferenceHydrated] = useState(false);
  const [blogPosts, setBlogPosts] = useState(() => normalizePreviewPosts(
    Array.isArray(texts?.__publicHomePayload?.blogPreviewPosts)
      ? texts.__publicHomePayload.blogPreviewPosts
      : HOME_BLOG_PREVIEW_POSTS
  ));
  const [shouldLoadExpandedBlogPreview, setShouldLoadExpandedBlogPreview] = useState(false);

  useBrowserLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setZoomLevel(readStoredZoomLevel());
    } catch {
      // noop
    } finally {
      setZoomPreferenceHydrated(true);
    }
  }, []);

  useBrowserLayoutEffect(() => {
    if (typeof window === 'undefined' || !zoomPreferenceHydrated) return;
    const nextZoomLevel = normalizeZoomLevel(zoomLevel);
    if (nextZoomLevel !== zoomLevel) {
      setZoomLevel(nextZoomLevel);
      return;
    }
    try {
      localStorage.setItem('zoomLevel', String(nextZoomLevel));
    } catch {
      // noop
    }
    const compactViewport = window.matchMedia(COMPACT_VIEWPORT_ZOOM_QUERY);
    const applyZoomLevel = () => {
      const appliedZoomLevel = resolveAppliedZoomLevel(nextZoomLevel, compactViewport.matches);
      // Root zoom also scales vh; the pinned showcase reel sizes itself to the
      // physical viewport with this variable, as AppContext does for the Studio.
      document.documentElement.style.setProperty(
        '--zoom-compensated-viewport-height',
        `${10000 / appliedZoomLevel}vh`
      );
      document.documentElement.style.zoom = `${appliedZoomLevel}%`;
    };

    applyZoomLevel();
    compactViewport.addEventListener?.('change', applyZoomLevel);
    return () => {
      compactViewport.removeEventListener?.('change', applyZoomLevel);
      document.documentElement.style.zoom = '';
      document.documentElement.style.removeProperty('--zoom-compensated-viewport-height');
    };
  }, [zoomLevel, zoomPreferenceHydrated]);

  useEffect(() => {
    if (shouldLoadExpandedBlogPreview) return undefined;
    if (typeof window === 'undefined') {
      setShouldLoadExpandedBlogPreview(true);
      return undefined;
    }

    const target = blogPreviewRef.current;
    if (!target || typeof window.IntersectionObserver !== 'function') {
      const fallbackTimer = window.setTimeout(() => {
        setShouldLoadExpandedBlogPreview(true);
      }, 5000);
      return () => window.clearTimeout(fallbackTimer);
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setShouldLoadExpandedBlogPreview(true);
        observer.disconnect();
      },
      { rootMargin: '320px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [shouldLoadExpandedBlogPreview]);

  useEffect(() => {
    if (!shouldLoadExpandedBlogPreview) return undefined;

    let cancelled = false;
    let cancelIdleTask = () => {};

    const loadBlogPosts = async () => {
      let staticPosts = Array.isArray(HOME_BLOG_PREVIEW_POSTS) ? HOME_BLOG_PREVIEW_POSTS : [];
      let staticTranslationMap = {};
      if (language !== 'en') {
        try {
          staticTranslationMap = await loadBlogTranslations(language);
        } catch (error) {
          console.warn(`Failed to load ${language} blog translations:`, error);
        }
      }

      let dynamicPosts = [];
      try {
        const { getPublishedPosts } = await import('../services/blogService');
        dynamicPosts = await getPublishedPosts();
      } catch (error) {
        console.warn('Failed to load Firestore blog posts:', error);
      }

      const dynamicTranslationMap = buildDynamicBlogTranslationMap(dynamicPosts, language);
      const translationMap = mergeBlogTranslationMaps(staticTranslationMap, dynamicTranslationMap);
      const translatedSlugs = language === 'en' ? null : new Set(Object.keys(translationMap));
      staticPosts = staticPosts.map((post) => mergeBlogPostContent(post, translationMap?.[post.slug]));
      dynamicPosts = dynamicPosts.map((post) => mergeBlogPostContent(post, dynamicTranslationMap?.[post.slug]));

      const existingSlugs = new Set(staticPosts.map((post) => post.slug));
      const merged = [
        ...staticPosts,
        ...dynamicPosts.filter((post) => post.slug && !existingSlugs.has(post.slug))
      ];
      const localizedPosts = translatedSlugs
        ? merged.filter((post) => post?.slug && translatedSlugs.has(post.slug))
        : merged;

      if (!cancelled) {
        setBlogPosts(normalizePreviewPosts(localizedPosts));
      }
    };

    cancelIdleTask = scheduleIdleTask(() => {
      void loadBlogPosts();
    });

    return () => {
      cancelled = true;
      cancelIdleTask();
    };
  }, [language, shouldLoadExpandedBlogPreview]);

  useEffect(() => {
    if (!user?.uid) return;
    if (authWarmupStartedRef.current) return;

    return scheduleIdleTask(() => {
      if (authWarmupStartedRef.current) return;
      authWarmupStartedRef.current = true;
      void warmAuthenticatedHomeRuntime();
    });
  }, [user?.uid]);

  const navigateTo = useCallback((path) => {
    if (typeof window === 'undefined') return;
    window.location.assign(localizeInternalHref(path, language));
  }, [language]);

  const navigateToMetadata = useCallback((payload = null, path = '/metadata') => {
    if (payload) {
      writePublicHomeHandoff(payload);
    }
    navigateTo(path);
  }, [navigateTo]);

  const handleToggleTheme = useCallback(() => {
    setTheme(getNextTheme(theme));
  }, [setTheme, theme]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 10, MAX_ZOOM_LEVEL));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 10, MIN_ZOOM_LEVEL));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(DEFAULT_ZOOM_LEVEL);
  }, []);

  const handleOpenStudio = useCallback(() => {
    navigateToMetadata({ action: 'openStudio' });
  }, [navigateToMetadata]);

  const handleStartDemo = useCallback(() => {
    navigateToMetadata({ action: 'startDemo' });
  }, [navigateToMetadata]);

  const handleOpenTour = useCallback(() => {
    navigateToMetadata({ action: 'openTour' });
  }, [navigateToMetadata]);

  const handleOpenLogin = useCallback(() => {
    navigateTo('/metadata?auth=login');
  }, [navigateTo]);

  const handleOpenPricing = useCallback(() => {
    navigateTo('/pricing');
  }, [navigateTo]);

  const handleOpenHistory = useCallback(() => {
    navigateTo('/metadata?view=history');
  }, [navigateTo]);

  const handleRunTrial = useCallback(async (trialInput, platformId) => {
    let payload = trialInput;

    if (typeof File !== 'undefined' && trialInput instanceof File) {
      try {
        payload = {
          imageData: await fileToDataUrl(trialInput),
          fileName: trialInput.name || GUEST_TRIAL_FILENAME,
          mimeType: trialInput.type || GUEST_TRIAL_MIME
        };
      } catch {
        payload = null;
      }
    }

    navigateToMetadata({
      action: 'runTrial',
      payload: {
        ...(payload || {}),
        platformId
      }
    });
  }, [navigateToMetadata]);

  const handleOpenBlog = useCallback((slug) => {
    navigateTo(localizeSeoHref(`/blog/${slug}`, language));
  }, [language, navigateTo]);

  const handleOpenBlogIndex = useCallback(() => {
    navigateTo(localizeSeoHref('/blog', language));
  }, [language, navigateTo]);

  const isAuthResolved = !loading;
  const isAuthenticated = isAuthResolved && Boolean(user);
  const isGuest = !isAuthenticated;
  const isUnlimitedPlan = isAuthResolved && user?.plan === 'BYOK';
  const userPlanCategory = getPlanCategory(user?.plan || 'free');
  const isFreeMember = isAuthenticated && userPlanCategory === PLAN_CATEGORIES.FREE;
  const skipToMainLabel = resolveLocalizedUiLabel(HOME_SKIP_TO_MAIN_LABELS, language);
  const mainContentLabel = resolveLocalizedUiLabel(HOME_MAIN_CONTENT_LABELS, language);
  const homeAccountLabel = isAuthenticated
    ? (user?.displayName || user?.email || texts?.sidebar_workspace)
    : texts?.sidebar_login;
  const localizedStudioHref = localizeInternalHref('/metadata', language);
  const localizedLoginHref = localizeInternalHref('/metadata?auth=login', language);
  const localizedHistoryHref = localizeInternalHref('/metadata?view=history', language);
  const localizedPricingHref = localizeInternalHref('/pricing', language);

  return (
    <>
      <a className="app-skip-link" href="#main-content">{skipToMainLabel}</a>
      <div
        className="app-container public-home-app public-marketing-shell"
        data-theme={theme}
      >
        <PublicMarketingHeader
          language={language}
          setLanguage={setLanguage}
          texts={texts}
          theme={theme}
          toggleTheme={handleToggleTheme}
          zoomLevel={zoomLevel}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onZoomReset={handleZoomReset}
          isAuthenticated={isAuthenticated}
          authLoading={loading}
          accountLabel={homeAccountLabel}
          studioHref={localizedStudioHref}
          loginHref={localizedLoginHref}
          accountHref={localizedStudioHref}
          historyHref={localizedHistoryHref}
          pricingHref={localizedPricingHref}
          tourHref={localizedStudioHref}
          onOpenStudio={handleOpenStudio}
          onOpenLogin={handleOpenLogin}
          onOpenHistory={handleOpenHistory}
          onOpenPricing={handleOpenPricing}
          onStartTour={handleOpenTour}
        />

        <main
          className="public-home-content public-marketing-main"
          id="main-content"
          tabIndex={-1}
          aria-label={mainContentLabel}
          aria-busy={loading ? 'true' : undefined}
        >
          <GuestLanding
            variant="public"
            theme={theme}
            themeReady={preferencesHydrated}
            onScrollToStudio={handleOpenStudio}
            onStartDemo={handleStartDemo}
            onOpenTour={handleOpenTour}
            onOpenLogin={handleOpenLogin}
            onOpenPricing={handleOpenPricing}
            studioHref={localizedStudioHref}
            demoHref={localizedStudioHref}
            tourHref={localizedStudioHref}
            loginHref={localizedLoginHref}
            pricingHref={localizedPricingHref}
            demoStarted={false}
            trialImageSrc={GUEST_TRIAL_IMAGE}
            trialPlatforms={GUEST_TRIAL_PLATFORMS}
            onRunTrial={handleRunTrial}
            trialLoading={false}
            trialError=""
            authLoading={loading}
            isAuthenticated={isAuthenticated}
            isFreeMember={isFreeMember}
            isUnlimitedPlan={isUnlimitedPlan}
            blogPosts={blogPosts}
            blogPreviewRef={blogPreviewRef}
            onOpenBlog={handleOpenBlog}
            onOpenBlogIndex={handleOpenBlogIndex}
            isGuest={isGuest}
          />
        </main>

        <PublicMarketingFooter
          language={language}
          setLanguage={setLanguage}
          texts={texts}
          theme={theme}
          toggleTheme={handleToggleTheme}
          isAuthenticated={isAuthenticated}
          authLoading={loading}
          accountLabel={homeAccountLabel}
          studioHref={localizedStudioHref}
          loginHref={localizedLoginHref}
          accountHref={localizedStudioHref}
          historyHref={localizedHistoryHref}
          pricingHref={localizedPricingHref}
          tourHref={localizedStudioHref}
          onOpenStudio={handleOpenStudio}
          onOpenLogin={handleOpenLogin}
          onOpenHistory={handleOpenHistory}
          onOpenPricing={handleOpenPricing}
          onStartTour={handleOpenTour}
          hrefLangEntries={HOME_HREFLANG_ENTRIES}
        />
      </div>
    </>
  );
};

const HomePage = () => <HomePageInner />;

export default HomePage;
